/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */

import styled from "styled-components/macro";
import { IconProps, AMPMIconProps, ArrowIconProps } from "./helpers";
import { dateTimePickerZIndex, fieldsBorderRadius } from "../constants";
import { colorState, spacing, typography } from "../../Foundation";

export interface StateTypes {
    isFocused: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    selectProps: {
        dropdownColor: string;
        menuIsOpen: boolean;
    };
}

export interface DateTimePickerProps {
    fullWidth?: boolean;
    popperWidth?: number;
}

export const Main = styled.div`
    position: relative;

    .text-entry-pointer {
        caret-color: transparent;
    }
`;

export const Icon = styled.div`
    pointer-events: none;
    right: 0;
    position: absolute;
    margin: ${spacing.spacing_6};
    z-index: 1;
`;

export const ArrowIcon = styled(Icon)<ArrowIconProps>`
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #595959;
    margin-right: ${(props) => (props.marginright ? "10px" : "40px")};
    margin-top: 1rem;
    z-index: 1;
`;

export const AMPMIcon = styled.div<IconProps>`
    background-color: ${(props: AMPMIconProps): string =>
        props.active ? colorState.surface.brand.primary : "none"};
    z-index: 1;
    justify-content: center;
    align-items: center;
    display: flex;
`;

export const calendarDropdownStyles = {
    option: (provided: object, state: StateTypes): object => ({
        ...provided,
        "&:hover": {
            backgroundColor: "white",
            color: "#4692A9",
            fontWeight: 400
        },
        background: "none",

        // Colours are not in theme
        color: state.isSelected ? "#4D4D4D" : "#595959",
        fontSize: "14px",
        fontWeight: 400
    }),

    singleValue: (provided: object): object => ({
        ...provided,
        color: "#4d4d4d",
        fontSize: "16px",
        fontWeight: 400
    })
};

export const TimeConverter = styled(AMPMIcon)<IconProps>`
    border-radius: 0 ${(props): string => props.theme.borderRadius} 0 0;
    height: 50%;
    padding: 0 5px 0 5px;
    cursor: pointer;
    border-left: 1px solid ${(props): string => props.theme.colors.neutral.light.backgroundColor};

    b {
        font-size: 12px;
        color: ${(props: AMPMIconProps): string =>
            props.active
                ? props.theme.colors.primary.deep.color
                : props.theme.colors.neutral.dark.backgroundColor};
        font-weight: 500;
    }

    &:last-child {
        border-radius: 0 0 ${(props): string => props.theme.borderRadius} 0;
    }
`;

export const DateWrapper = styled.div<DateTimePickerProps>`
    outline: none;

    input[disabled] {
        background-color: ${colorState.actions.input.disabled} !important;
    }

    .react-datepicker__time-container {
        width: auto;

        .react-datepicker__time {
            .react-datepicker__time-box {
                padding: ${spacing.spacing_4} 0;

                ul.react-datepicker__time-list {
                    margin: ${spacing.spacing_4} 0;

                    li.react-datepicker__time-list-item {
                        color: ${(props): string =>
                            props.theme.colors.neutral.dark.backgroundColor};
                        padding: ${spacing.spacing_8} ${spacing.spacing_12};
                        height: fit-content;
                        display: flex;
                        align-items: center;
                        font-size: ${typography.size.ts};
                        font-weight: ${typography.weight.medium};

                        &:hover {
                            background-color: #f0f9fc;
                            color: ${(props): string =>
                                props.theme.colors.primary.main.backgroundColor};
                            font-weight: 700;
                        }
                    }

                    li.react-datepicker__time-list-item--selected {
                        background-color: #f0f9fc;
                        color: #157593;
                        font-weight: 500;
                    }
                }
            }
        }
    }

    .react-datepicker--time-only,
    .react-datepicker__time-container,
    .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
        width: 100%;
        text-align: left;
    }

    .react-datepicker-popper {
        width: ${(props) =>
            props.fullWidth
                ? "100%"
                : props.popperWidth
                ? `${props.popperWidth}px`
                : "fit-content"};
        overflow: auto !important;
        z-index: ${dateTimePickerZIndex};
        padding-top: 0 !important;
        font-size: 14px;
    }

    .react-datepicker {
        border: 1px solid ${(props): string => props.theme.colors.primary.main.backgroundColor};
        box-shadow: 0 3px 8px ${(props): string => props.theme.colors.neutral.light.backgroundColor};
        overflow: hidden;

        .react-datepicker__header--time {
            display: none;
        }

        .react-datepicker__header {
            background-color: ${(props): string => props.theme.colors.primary.deep.color};
            border-bottom: 1px solid
                ${(props): string => props.theme.colors.neutral.light.backgroundColor};
            width: 100%;
            text-align: left;

            .react-datepicker__current-month,
            .react-datepicker-time__header,
            .react-datepicker-year-header {
                color: ${(props): string => props.theme.colors.neutral.darker.backgroundColor};
                font-size: 16px;
                font-weight: 500;
            }

            .react-datepicker__day-name {
                color: ${(props): string => props.theme.colors.primary.main.backgroundColor};
                text-transform: uppercase;
                font-size: 14px;
                font-weight: 500;
                margin: ${spacing.spacing_4};
            }
        }

        .react-datepicker__day {
            margin: ${spacing.spacing_4};
        }

        .react-datepicker__day:hover {
            background-color: ${(props): string => props.theme.colors.primary.main.backgroundColor};
            border-radius: 100%;
            color: ${(props): string => props.theme.colors.primary.deep.color};
        }

        .react-datepicker__month .react-datepicker__day {
            font-size: 14px;
            font-weight: 500;

            &:focus {
                outline: none;
            }
        }

        .react-datepicker__month-container {
            padding: ${spacing.spacing_10};
        }

        .react-datepicker__month .react-datepicker__day--keyboard-selected {
            background-color: ${(props): string => props.theme.colors.primary.main.backgroundColor};
            border-radius: 100%;
            color: ${(props): string => props.theme.colors.primary.deep.color};
        }

        .react-datepicker__day--selected {
            border-radius: 100%;
            color: ${(props): string => props.theme.colors.primary.deep.color};
            background-color: ${(props): string => props.theme.colors.primary.main.backgroundColor};
        }
    }
`;
const spacedFlexDiv = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const DatePickerHeader = styled(spacedFlexDiv)`
    margin: ${spacing.spacing_10};
    align-items: center;
    justify-content: center;

    .left-arrow {
        transform: rotate(180deg);
    }

    .monthyear-heading {
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0.3px;
        color: ${(props): string => props.theme.colors.neutral.darker.backgroundColor};
    }

    .home {
        font-size: 23px;
        color: #4692a9;
    }

    .flex-content {
        flex: 1;
    }
`;

export const TimeWrapper = styled(spacedFlexDiv)`
    position: absolute;
    right: 0;
    height: 38px;
    width: auto;
    flex-direction: column;
    border: solid 1px #ced4da;
    border-top-right-radius: ${fieldsBorderRadius};
    border-bottom-right-radius: ${fieldsBorderRadius};
`;

export const Image = styled.img`
    cursor: pointer;

    &:focus {
        background: none;
        border: none;
        outline: none;
    }
`;
